import { apiClient } from '@api';
import { useApplicationStore } from '@data';
import { LeadData } from '@qmd-types/onboarding';
import {
  Provider,
  ProviderAvailability,
  ReservedVisit,
  ServiceType,
} from '@types';
import { isAxiosError } from 'axios';

interface FetchAvailabilitiesParams {
  fromDateTime: string;
  providerId?: string;
  state: string;
  timeZone: string;
  toDateTime: string;
  visitTypeId: string;
}

export async function fetchAvailabilities({
  providerId,
  fromDateTime,
  state,
  timeZone,
  toDateTime,
  visitTypeId,
}: FetchAvailabilitiesParams): Promise<ProviderAvailability[]> {
  const { flags } = useApplicationStore.getState();
  const isPatientVisibilityOverrideDisabled =
    (!flags.useProviderVisibility).toString();
  const queryParams = new URLSearchParams({
    providerId: providerId || '',
    fromDateTime,
    stateCode: state,
    timeZone,
    toDateTime,
    visitTypeId,
    isPatientVisibilityOverrideDisabled,
  }).toString();
  const { data } = await apiClient.get<ProviderAvailability[]>(
    `/api/v1/onboarding/availability?${queryParams}`,
  );

  return data;
}

export async function fetchServiceTypesByState(
  state: string,
): Promise<ServiceType[]> {
  const { flags } = useApplicationStore.getState();
  const isPatientVisibilityOverrideDisabled = !flags.useProviderVisibility;
  const { data } = await apiClient.get<ServiceType[]>(
    `/api/v1/onboarding/serviceTypes?stateCode=${state}&isPatientVisibilityOverrideDisabled=${isPatientVisibilityOverrideDisabled}`,
  );

  return data;
}

export async function fetchProvidersByStateAndVisitType(
  stateCode: string,
  visitTypeId: string,
): Promise<Provider[]> {
  const { flags } = useApplicationStore.getState();
  const isPatientVisibilityOverrideDisabled = !flags.useProviderVisibility;
  const { data } = await apiClient.get<Provider[]>(
    `/api/v1/onboarding/providers?stateCode=${stateCode}&visitTypeId=${visitTypeId}&isPatientVisibilityOverrideDisabled=${isPatientVisibilityOverrideDisabled}`,
  );

  return data;
}

export async function logOnboardingPatient(params: LeadData): Promise<void> {
  try {
    await apiClient.post('/api/v1/onboarding/log', params);
  } catch (error) {
    if (isAxiosError(error)) {
      throw error;
    }

    console.error('An unexpected error occurred', error);
    throw new Error('An unexpected error occurred');
  }
}

interface ReserveVisitParams {
  dateTime: string;
  phoneNumberVerificationToken: string;
  providerId: string;
  stateCode: string;
  timeZone: string;
  visitTypeId: string;
}

export async function reserveVisit(
  params: ReserveVisitParams,
): Promise<ReservedVisit> {
  try {
    const { data } = await apiClient.post<ReservedVisit>(
      '/api/v1/onboarding/reservations',
      params,
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw error;
    }

    console.error('An unexpected error occurred', error);
    throw new Error('An unexpected error occurred');
  }
}

export async function cancelReservation(reservationId: string) {
  await apiClient.delete(`/api/v1/onboarding/reservations/${reservationId}`);
}
